import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Calendar, ClipboardList } from 'lucide-react';
import { taskApi, Task, TaskPriority, CreateTaskData, UpdateTaskData } from '@/services/taskApi';
import { employeeApi } from '@/services/employeeApi';
import { branchApi } from '@/services/branchApi';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import SimpleSelect, { type SimpleSelectOption } from '@/components/common/SimpleSelect';
import DatePicker from '@/components/common/DatePicker';

const PRIORITY_OPTIONS: SimpleSelectOption[] = [
  { value: TaskPriority.LOW, label: 'Low' },
  { value: TaskPriority.MEDIUM, label: 'Medium' },
  { value: TaskPriority.HIGH, label: 'High' },
  { value: TaskPriority.URGENT, label: 'Urgent' },
];

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  editTask?: Task | null;
}

export function CreateTaskModal({ isOpen, onClose, editTask }: CreateTaskModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TaskPriority>(TaskPriority.MEDIUM);
  const [assignedToId, setAssignedToId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [branchId, setBranchId] = useState('');

  // Check if user needs branch selection (no activeBranch assigned)
  const needsBranchSelection = !user?.activeBranch?.id;

  // Fetch branches for selection if user has no branch
  const { data: branches } = useQuery({
    queryKey: ['branches-simple-list'],
    queryFn: () => branchApi.getBranchList(),
    enabled: isOpen && needsBranchSelection,
  });

  // Fetch employees
  const { data: employeesData } = useQuery({
    queryKey: ['employees-for-tasks'],
    queryFn: () => employeeApi.getEmployees({ limit: 100 }),
    enabled: isOpen,
  });

  // Filter staff (exclude super admin, admin, manager)
  const staffMembers = employeesData?.data?.filter((emp: any) =>
    ['TECHNICIAN', 'RECEPTIONIST', 'BRANCH_ADMIN', 'SERVICE_ADMIN', 'COUNTER_ADMIN', 'STOCK_ADMIN'].includes(emp.role)
  ) || [];

  // Reset form when modal opens/closes or edit task changes
  useEffect(() => {
    if (editTask) {
      setTitle(editTask.title);
      setDescription(editTask.description || '');
      setPriority(editTask.priority);
      setAssignedToId(editTask.assignedToId);
      setDueDate(new Date(editTask.dueDate).toISOString().split('T')[0]);
      setNotes(editTask.notes || '');
    } else {
      resetForm();
    }
  }, [editTask, isOpen]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setPriority(TaskPriority.MEDIUM);
    setAssignedToId('');
    setDueDate('');
    setNotes('');
    setBranchId('');
  };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTaskData) => taskApi.createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-stats'] });
      toast.success('Task created successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create task');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskData }) => taskApi.updateTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task-stats'] });
      toast.success('Task updated successfully');
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update task');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }

    if (!assignedToId) {
      toast.error('Please select an assignee');
      return;
    }

    if (!dueDate) {
      toast.error('Due date is required');
      return;
    }

    // Validate branch selection for users without assigned branch
    if (!user?.activeBranch?.id && !branchId) {
      toast.error('Please select a branch');
      return;
    }

    if (editTask) {
      updateMutation.mutate({
        id: editTask.id,
        data: {
          title,
          description: description || undefined,
          priority,
          assignedToId,
          dueDate,
          notes: notes || undefined,
        },
      });
    } else {
      createMutation.mutate({
        title,
        description: description || undefined,
        priority,
        assignedToId,
        dueDate,
        notes: notes || undefined,
        branchId: user?.activeBranch?.id || branchId || undefined,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 bg-white">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ClipboardList className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-gray-900">
              {editTask ? 'Edit Task' : 'Create Task'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter task title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter task description (optional)"
            />
          </div>

          {/* Branch Selection (only for users without assigned branch) */}
          {needsBranchSelection && !editTask && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch <span className="text-red-500">*</span>
              </label>
              <SimpleSelect
                value={branchId}
                onChange={setBranchId}
                options={[
                  { value: '', label: 'Select branch' },
                  ...(branches ?? []).map((branch) => ({
                    value: branch.id,
                    label: branch.name + ' (' + branch.code + ')',
                  })),
                ]}
                placeholder="Select branch"
                aria-label="Branch"
                wrapperClassName="w-full min-w-0"
                className="focus:!ring-blue-500 focus:!border-blue-500"
              />
            </div>
          )}

          {/* Assignee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign To <span className="text-red-500">*</span>
            </label>
            <SimpleSelect
              value={assignedToId}
              onChange={setAssignedToId}
              options={[
                { value: '', label: 'Select assignee' },
                ...staffMembers.map((emp: any) => ({
                  value: emp.id,
                  label: emp.name + ' (' + emp.role + ')',
                })),
              ]}
              placeholder="Select assignee"
              aria-label="Assignee"
              wrapperClassName="w-full min-w-0"
              className="focus:!ring-blue-500 focus:!border-blue-500"
            />
          </div>

          {/* Priority & Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <SimpleSelect
                value={priority}
                onChange={(v) => setPriority(v as TaskPriority)}
                options={PRIORITY_OPTIONS}
                aria-label="Priority"
                wrapperClassName="w-full min-w-0"
                className="focus:!ring-blue-500 focus:!border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                wrapperClassName="w-full min-w-0"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Additional notes (optional)"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : editTask
                ? 'Update Task'
                : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
